import { Box, Divider, Stack, type SxProps, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { type FC, type ReactNode, useMemo } from "react";

import { calcNoPrice, formatCents } from "~/utils/formatPrice";

export interface PriceFieldProps {
  price: number;
  priceNo: number | null;
  withText?: boolean;
  padding?: string;
  children?: ReactNode;
  textSx?: SxProps;
  NA?: boolean;
}

export const PriceField: FC<PriceFieldProps> = ({
  price = 0,
  priceNo,
  withText = false,
  padding = "0.8rem 0",
  children,
  textSx,
  NA = false,
}) => {
  const { yesValue, noValue } = useMemo(() => {
    const yes = price;
    const no = priceNo === null ? calcNoPrice(yes) : priceNo;

    return {
      yesValue: formatCents(yes),
      noValue: formatCents(no),
    };
  }, [price, priceNo]);

  const minWidth = useMemo(() => {
    return withText ? 80 : 35;
  }, [withText]);

  if (NA) {
    return <Typography variant="caption">N/A</Typography>;
  }

  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        padding: padding,
        position: "relative"
      }}>
      {children}
      <Typography
        variant="caption"
        sx={[{
          color: "success.main",
          minWidth: minWidth,
          textAlign: "center"
        }, ...(Array.isArray(textSx) ? textSx : [textSx])]}>
        {withText ? "Yes " : <Box component="span" sx={visuallyHidden}>Yes </Box>}
        {yesValue}
      </Typography>
      <Divider orientation="vertical" flexItem />
      <Typography
        variant="caption"
        sx={[{
          color: "error.main",
          minWidth: minWidth,
          textAlign: "center"
        }, ...(Array.isArray(textSx) ? textSx : [textSx])]}>
        {withText ? "No " : <Box component="span" sx={visuallyHidden}>No </Box>}
        {noValue}
      </Typography>
    </Stack>
  );
};
